import { motion } from "framer-motion";
import { HandHeart, Home, Sprout, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { HISTORIA_IMAGE, HERO_IMAGE, BRAND } from "@/config";
import { Ornament } from "@/components/Ornament";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

const VALUES = [
  { icon: HandHeart, title: "Hecho a mano", text: "Tandas pequeñas, cada frasco pasa por nuestras manos. Nada de líneas industriales." },
  { icon: Users, title: "Productores locales", text: "Trabajamos de la mano con productores locales de Yucatán, eligiendo cada chile e ingrediente directamente de manos que conocen la tierra. Así garantizamos frescura, apoyamos a las comunidades de la región y mantenemos viva la esencia de cada receta." },
  { icon: Sprout, title: "Ingredientes artesanales", text: "Seleccionamos cada ingrediente como si cocináramos para nuestra propia mesa. Porque lo hacemos." },
  { icon: Home, title: "Familia", text: "Somos una familia meridana que convirtió su sazón en un proyecto de vida." },
];

export default function Historia() {
  return (
    <div data-testid="historia-page">
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8">
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-script font-semibold text-3xl text-orange">
          Herencia y tradición que es toda una explosión de sabor y sensación
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mt-4 max-w-3xl font-script text-4xl font-semibold leading-tight text-cocoa sm:text-5xl lg:text-6xl text-balance"
        >
          Una cocina de Mérida, una familia y mucho habanero
        </motion.h1>
      </section>

      <section className="mx-auto grid max-w-7xl items-center gap-12 px-4 pb-24 sm:px-8 lg:grid-cols-2">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
          <img src={HISTORIA_IMAGE} alt="Cocina familiar en Mérida preparando salsa en molcajete" className="w-full rounded-3xl border border-sand object-cover shadow-xl" />
        </motion.div>
        <motion.div
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.15 } } }}
          initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
          className="space-y-6"
        >
          <motion.p variants={fadeUp} className="leading-relaxed text-clay md:text-lg">
            SoyDiVa nace a raíz de la mudanza de nuestra familia a Mérida, en busca de una vida digna y con
            la urgente necesidad de proveer para los nuestros.
          </motion.p>
          <motion.p variants={fadeUp} className="leading-relaxed text-clay md:text-lg">
            En septiembre de 2017, guiados por la receta de Salsa Macha de la Abuela Mima, le añadimos toques
            especiales, mucho amor e ilusión, y comenzamos vendiendo 14 frascos casa por casa. Así vivimos
            durante un año.
          </motion.p>
          <motion.p variants={fadeUp} className="leading-relaxed text-clay md:text-lg">
            Hoy, gracias a las oportunidades que nos brinda este estado, estamos mucho más capacitados para
            seguir haciendo crecer este gran proyecto familiar.
          </motion.p>
          <motion.p variants={fadeUp} className="font-script font-semibold text-3xl text-orange">
            "El sabor que llena de alegría tu corazón"
          </motion.p>
        </motion.div>
      </section>

      <section className="mx-auto max-w-3xl px-4 pb-24 text-center sm:px-8">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
          <p className="font-script font-semibold text-3xl text-orange">Nuestra visión</p>
          <Ornament className="mt-4 text-terracotta" />
          <p className="mt-6 font-display text-2xl font-semibold leading-relaxed text-cocoa md:text-3xl text-balance" data-testid="vision-block">
            Convertir nuestro proyecto familiar en una empresa con presencia internacional, reconocida por
            preservar la tradición, la pureza y la excelencia gastronómica de México en cada una de nuestras
            creaciones.
          </p>
        </motion.div>
      </section>

      <section className="relative overflow-hidden bg-cocoa py-24 text-white">
        <span className="pointer-events-none absolute -left-12 top-10 h-40 w-40 rounded-full bg-rosa/20" aria-hidden="true" />
        <span className="pointer-events-none absolute -right-10 bottom-10 h-24 w-24 rotate-45 rounded-2xl bg-terracotta/30" aria-hidden="true" />
        <span className="pointer-events-none absolute right-1/4 top-8 h-4 w-4 rotate-45 bg-corn/60" aria-hidden="true" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-8">
          <p className="font-script text-2xl font-semibold text-corn">Míranos en acción</p>
          <h2 className="mt-2 font-script text-3xl font-semibold sm:text-4xl">La historia de SoyDiVa en video</h2>
          <Ornament className="mt-4 text-terracotta" />
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="mt-10 overflow-hidden rounded-3xl border-4 border-white/15 shadow-2xl"
            data-testid="historia-video"
          >
            <div className="aspect-video">
              <iframe
                src="https://www.youtube.com/embed/9rEp0sOdgW0"
                title="Video de SoyDiVa — Nuestra historia"
                className="h-full w-full"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-surface">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8">
          <h2 className="text-center font-script text-3xl font-semibold text-cocoa sm:text-4xl">Lo que nos mueve</h2>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="rounded-2xl border border-sand bg-white p-8"
                data-testid={`value-${i}`}
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-habanero/10 text-habanero">
                  <v.icon size={22} />
                </span>
                <h3 className="mt-4 font-display text-xl font-bold text-cocoa">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-clay">{v.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-24 sm:px-8 lg:grid-cols-2">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="order-2 lg:order-1">
          <h2 className="font-script text-3xl font-semibold text-cocoa sm:text-4xl text-balance">
            De Yucatán a tu mesa, donde sea que estés
          </h2>
          <p className="mt-6 leading-relaxed text-clay md:text-lg">
            Hoy puedes pedir tus salsas favoritas desde este sitio, con envío a domicilio o pick up con cita previa
            en Fraccionamiento Las Américas, Mérida. La misma salsa de siempre, ahora a un clic de distancia.
          </p>
          <Button asChild className="mt-8 rounded-full bg-habanero px-8 py-6 font-bold text-white hover:bg-habanero-dark" data-testid="historia-shop-cta">
            <Link to="/tienda">Probar las salsas</Link>
          </Button>
        </motion.div>
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="order-1 lg:order-2">
          <img src={HERO_IMAGE} alt="Frascos de salsas SoyDiVa con ingredientes frescos" loading="lazy" className="w-full rounded-3xl border border-sand object-cover shadow-xl" />
        </motion.div>
      </section>
    </div>
  );
}
